// public/v3/src/lib/bhi-retag.js — untagged behavioral-time review helpers.
//
// entryProgramFamily defaults untagged activity entries to the CCM family, so
// behavioral time logged without a program tag (legacy v2 entries, pre-preset
// logging) silently counts toward CCM and never satisfies the 99484 gate.
// These helpers find the reviewable slice — untagged entries with behavioral
// evidence in their own text, for BHI-enrolled patients, in recent months no
// bill has touched — and retag ONE entry at a time on explicit operator
// action. Deliberately no bulk path: retagging manufactures BHI minutes if it
// isn't evidence-based per entry.
//
// CommonJS so Jest can require it directly (same pattern as bhi-billing.js).

// Behavioral evidence in the entry's own text. Conservative by construction:
// a term here should be hard to read as anything but behavioral-health work.
const BH_EVIDENCE_RE = /\b(phq[-\s]?[29]?|gad[-\s]?7|depress\w*|anxiet\w*|behaviou?ral\s+health|mental\s+health|psychiatr\w*|psycholog\w*|psychotrop\w*|bhi\b|suicid\w*|therapist|psychotherap\w*|counsel(ing|or)|substance\s+use|\bsud\b|mood\s+(disorder|check|screen)\w*)/i;

function bhEvidenceMatch(entry) {
  if (!entry) return null;
  for (const field of ['desc', 'notes', 'label', 'type']) {
    const m = BH_EVIDENCE_RE.exec(String(entry[field] || ''));
    if (m) return m[0];
  }
  return null;
}

// Months eligible for review: current plus (monthsBack - 1) prior. Retagging
// further back changes minute pools under months that are long closed.
function _recentMonths(nowMs, monthsBack) {
  const out = [];
  const d = new Date(nowMs);
  for (let i = 0; i < monthsBack; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(m.getFullYear() + '-' + String(m.getMonth() + 1).padStart(2, '0'));
  }
  return out;
}

// A month any push has touched (pending, pushed, or parked) is off-limits:
// moving minutes between families under a claimed/billed month invalidates
// what the signed note attested.
function _monthTouchedByPush(raw, month) {
  const pushes = (raw && raw.billingPush) || {};
  return !!(pushes[month + '|ccm'] || pushes[month + '|bhi']);
}

// Reviewable candidates for one patient. Requires an actual BHI enrollment —
// without billing.bhiProgram there is no code to retag the entry to.
function untaggedBhCandidates(raw, { nowMs = Date.now(), monthsBack = 3 } = {}) {
  if (!raw || typeof raw !== 'object') return [];
  const bhiProgram = String((raw.billing || {}).bhiProgram || '').trim();
  if (!bhiProgram || bhiProgram.toLowerCase() === 'none') return [];
  const months = _recentMonths(nowMs, monthsBack).filter((m) => !_monthTouchedByPush(raw, m));
  if (!months.length) return [];
  const log = Array.isArray(raw.activityLog) ? raw.activityLog : [];
  const out = [];
  for (const e of log) {
    if (!e || e.isConsolidatedNote || e.program) continue;
    const month = String(e.date || '').slice(0, 7);
    if (!months.includes(month)) continue;
    const evidence = bhEvidenceMatch(e);
    if (!evidence) continue;
    out.push({ entry: e, month, evidence, toProgram: bhiProgram });
  }
  out.sort((a, b) => String(b.entry.date || '').localeCompare(String(a.entry.date || '')));
  return out;
}

// Pure blob transform: tag one entry with the patient's BHI code, stamping
// provenance so an auditor can see the entry was reclassified, when, and that
// it was previously untagged. Returns null when the entry is no longer
// eligible (already tagged, missing, or enrollment gone).
function retagEntryToBhi(raw, entryId, nowIso) {
  if (!raw || typeof raw !== 'object') return null;
  const bhiProgram = String((raw.billing || {}).bhiProgram || '').trim();
  if (!bhiProgram || bhiProgram.toLowerCase() === 'none') return null;
  const log = Array.isArray(raw.activityLog) ? raw.activityLog : [];
  const idx = log.findIndex((e) => e && e.id === entryId && !e.program && !e.isConsolidatedNote);
  if (idx === -1) return null;
  const nextLog = log.slice();
  nextLog[idx] = {
    ...nextLog[idx],
    program: bhiProgram,
    retag: { at: nowIso || new Date().toISOString(), from: 'untagged' },
  };
  return { ...raw, activityLog: nextLog };
}

module.exports = {
  BH_EVIDENCE_RE,
  bhEvidenceMatch,
  untaggedBhCandidates,
  retagEntryToBhi,
};
